import React, { createContext, useContext, useState, useEffect } from 'react';
import { DEFAULT_THEME, type ThemeColors } from '../constants/themeDefaults';
import { businessService } from '../services/businessService';

interface BusinessThemeContextType {
    theme: ThemeColors;
    setTheme: (theme: ThemeColors) => void;
    businessId: string | null;
    setBusinessId: (id: string | null) => void;
}

const THEME_STORAGE_KEY = 'businessTheme';
const BUSINESS_ID_STORAGE_KEY = 'businessThemeId';

const BusinessThemeContext = createContext<BusinessThemeContextType | undefined>(undefined);

const isDefaultTheme = (theme: ThemeColors): boolean => {
    return Object.entries(theme).every(([key, value]) => value === DEFAULT_THEME[key as keyof ThemeColors]);
};

const validateThemeColors = (theme: any): ThemeColors => {
    return {
        backgroundColor: theme?.backgroundColor || DEFAULT_THEME.backgroundColor,
        primaryColor: theme?.primaryColor || DEFAULT_THEME.primaryColor,
        secondaryColor: theme?.secondaryColor || DEFAULT_THEME.secondaryColor,
        navigationColor: theme?.navigationColor || DEFAULT_THEME.navigationColor,
        textColor: theme?.textColor || DEFAULT_THEME.textColor,
        accentColor: theme?.accentColor || DEFAULT_THEME.accentColor,
        subtleAccent: theme?.subtleAccent || DEFAULT_THEME.subtleAccent,
        borderColor: theme?.borderColor || DEFAULT_THEME.borderColor
    };
};

// Helper function to check if a color value is valid (not empty string)
const isValidColor = (color: string): boolean => {
    return typeof color === 'string' && color.trim() !== '';
};

export const BusinessThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [theme, setTheme] = useState<ThemeColors>(() => {
        const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
        if (storedTheme) {
            try {
                const parsed = JSON.parse(storedTheme);
                // Check if any color values are empty strings and clear localStorage if so
                const colorKeys = ['backgroundColor', 'primaryColor', 'secondaryColor', 'navigationColor', 'textColor', 'accentColor', 'headingColor', 'subtleAccent', 'borderColor'];
                const hasInvalidColors = colorKeys.some(key => parsed[key] === '');
                if (hasInvalidColors) {
                    console.warn('Found invalid empty color values in stored theme, clearing localStorage');
                    localStorage.removeItem(THEME_STORAGE_KEY);
                    return DEFAULT_THEME;
                }
                // Validate parsed theme to ensure no empty color values
                return validateThemeColors(parsed);
            } catch (error) {
                console.error('Failed to parse stored theme:', error);
                localStorage.removeItem(THEME_STORAGE_KEY);
                return DEFAULT_THEME;
            }
        }
        return DEFAULT_THEME;
    });
    const [businessId, setBusinessId] = useState<string | null>(() => {
        return localStorage.getItem(BUSINESS_ID_STORAGE_KEY);
    });

    // Load theme data on mount and when businessId changes
    useEffect(() => {
        const loadTheme = async () => {
            if (businessId) {
                try {
                    const business = await businessService.getBusinessDetails(businessId);
                    if (business) {
                        const newTheme = validateThemeColors(business);
                        setTheme(newTheme);
                        localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(newTheme));
                        localStorage.setItem(BUSINESS_ID_STORAGE_KEY, businessId);
                    }
                } catch (error) {
                    console.error('Failed to load business theme:', error);
                    setTheme(DEFAULT_THEME);
                    localStorage.removeItem(THEME_STORAGE_KEY);
                }
            } else {
                setTheme(DEFAULT_THEME);
                localStorage.removeItem(THEME_STORAGE_KEY);
                localStorage.removeItem(BUSINESS_ID_STORAGE_KEY);
            }
        };

        // Only load theme if it's not already in localStorage
        if (businessId && !localStorage.getItem(THEME_STORAGE_KEY)) {
            loadTheme();
        }

        if (businessId && localStorage.getItem(THEME_STORAGE_KEY)) {
            const storedTheme = JSON.parse(localStorage.getItem(THEME_STORAGE_KEY) || '{}');
            // Check if any color values are empty strings and clear localStorage if so
            const colorKeys = ['backgroundColor', 'primaryColor', 'secondaryColor', 'navigationColor', 'textColor', 'accentColor', 'headingColor', 'subtleAccent', 'borderColor'];
            const hasInvalidColors = colorKeys.some(key => storedTheme[key] === '');
            if (hasInvalidColors) {
                console.warn('Found invalid empty color values in stored theme, clearing localStorage');
                localStorage.removeItem(THEME_STORAGE_KEY);
                setTheme(DEFAULT_THEME);
            } else {
                // Validate stored theme to ensure no empty color values
                const validatedTheme = validateThemeColors(storedTheme);
                setTheme(validatedTheme);
            }
        }
    }, [businessId]);

    // Update the theme-color meta tag when theme changes (for mobile PWA navigation bar)
    useEffect(() => {
        // Update theme-color for Android
        let metaThemeColor = document.querySelector('meta[name="theme-color"]');
        if (metaThemeColor) {
            // Remove and re-add to force Android to update
            metaThemeColor.remove();
        }
        
        // Create new meta tag with updated color
        metaThemeColor = document.createElement('meta');
        metaThemeColor.setAttribute('name', 'theme-color');
        metaThemeColor.setAttribute('content', theme.navigationColor);
        document.head.appendChild(metaThemeColor);
        
        // Update iOS status bar style to match navigation color
        const metaAppleStatusBar = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
        if (metaAppleStatusBar) {
            // Use black-translucent to allow the navigation color to show through
            metaAppleStatusBar.setAttribute('content', 'black-translucent');
        }
    }, [theme.navigationColor]);

    return (
        <BusinessThemeContext.Provider value={{ theme, setTheme, businessId, setBusinessId }}>
            {children}
        </BusinessThemeContext.Provider>
    );
};

export const useBusinessTheme = () => {
    const context = useContext(BusinessThemeContext);
    if (context === undefined) {
        throw new Error('useBusinessTheme must be used within a BusinessThemeProvider');
    }
    return context;
}; 